<template>
  <footer class="app-footer">
    <div class="footer-content">
      <div class="footer-section">
        <p class="footer-text">© {{ currentYear }} DietEdics</p>
        <p class="privacy-note">We will never sell your personal information. Ever.</p>
      </div>
      <div class="footer-section footer-controls">
        <button @click="handleThemeToggle" class="control-toggle" :aria-label="themeToggleLabel">
          <span class="control-icon">{{ themeIcon }}</span>
          <span class="control-text">{{ themeText }}</span>
        </button>
        <button @click="handleAccessibilityToggle" class="control-toggle" :aria-label="accessibilityToggleLabel">
          <span class="control-icon">{{ accessibilityIcon }}</span>
          <span class="control-text">{{ accessibilityText }}</span>
        </button>
      </div>
    </div>
  </footer>
</template>

<script>
import { getTheme, toggleTheme, getAccessibilityMode, toggleAccessibilityMode } from '../../config/theme.js'

export default {
  name: 'GlobalFooter',
  data() {
    return {
      currentTheme: 'light',
      accessibilityMode: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    themeIcon() {
      return this.currentTheme === 'light' ? '🌙' : '☀️'
    },
    themeText() {
      return this.currentTheme === 'light' ? 'Dark Mode' : 'Light Mode'
    },
    themeToggleLabel() {
      return `Switch to ${this.currentTheme === 'light' ? 'dark' : 'light'} mode`
    },
    accessibilityIcon() {
      return this.accessibilityMode ? '👁️' : '♿'
    },
    accessibilityText() {
      return this.accessibilityMode ? 'Colorblind Mode On' : 'Colorblind Mode Off'
    },
    accessibilityToggleLabel() {
      return `Turn colorblind accessibility mode ${this.accessibilityMode ? 'off' : 'on'}`
    }
  },
  mounted() {
    this.currentTheme = getTheme()
    this.accessibilityMode = getAccessibilityMode()
    // Listen for theme changes from other components
    window.addEventListener('theme-changed', this.handleThemeChange)
    window.addEventListener('accessibility-changed', this.handleAccessibilityChange)
  },
  beforeUnmount() {
    window.removeEventListener('theme-changed', this.handleThemeChange)
    window.removeEventListener('accessibility-changed', this.handleAccessibilityChange)
  },
  methods: {
    handleThemeToggle() {
      this.currentTheme = toggleTheme()
      // Dispatch event so other components can react
      window.dispatchEvent(new CustomEvent('theme-changed', { 
        detail: { theme: this.currentTheme } 
      }))
    },
    handleAccessibilityToggle() {
      this.accessibilityMode = toggleAccessibilityMode()
      // Dispatch event so other components can react
      window.dispatchEvent(new CustomEvent('accessibility-changed', { 
        detail: { enabled: this.accessibilityMode } 
      }))
    },
    handleThemeChange(event) {
      this.currentTheme = event.detail.theme
    },
    handleAccessibilityChange(event) {
      this.accessibilityMode = event.detail.enabled
    }
  }
}
</script>

<style scoped>
.app-footer {
  background-color: var(--color-surface-light);
  border-top: 1px solid var(--color-border-light);
  padding: 20px;
  margin-top: auto;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.footer-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.footer-controls {
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.footer-text {
  color: var(--color-text-secondary);
  margin: 0;
  font-size: 0.9rem;
}

.privacy-note {
  color: var(--color-text-secondary);
  margin: 0;
  font-size: 0.85rem;
  font-style: italic;
  opacity: 0.9;
}

.control-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-surface-elevated);
  border: 2px solid var(--color-border-main);
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--color-text-primary);
  transition: all 0.3s ease;
  font-family: inherit;
}

.control-toggle:hover {
  background-color: var(--color-primary-light);
  border-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.control-toggle:active {
  transform: translateY(0);
}

.control-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.control-text {
  font-weight: 500;
}

@media (max-width: 768px) {
  .footer-content {
    flex-direction: column;
    text-align: center;
  }
  
  .footer-controls {
    width: 100%;
    justify-content: center;
  }
  
  .control-toggle {
    flex: 1;
    min-width: 140px;
    justify-content: center;
  }
}
</style>

